// Single sign-on (design §4): verify a domain by TXT record, connect Entra ID by OIDC (app registration) or SAML
// (enterprise app), test it in a pop-up, then optionally require it and let new people in automatically (JIT).
import { useEffect, useState } from "react";
import { CheckCircle2, Copy, KeyRound, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { useMe } from "../App";
import { api } from "../lib/api";
import { startSsoTest } from "../lib/auth";
import { fullDate } from "../lib/format";
import type { AdminOverview, Idp } from "../lib/types";
import { Card, Chip, inputCls } from "../components/bits";
import { Button, ConfirmDialog, cx, toast } from "../components/ui";
import { MicrosoftMark } from "../components/icons";

export function CopyLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm">
      <div className="text-xs text-muted mb-0.5">{label}</div>
      <div className="flex items-center gap-1.5">
        <code className="flex-1 min-w-0 rounded-md bg-surface-2 px-2 py-1.5 text-xs break-all">{value}</code>
        <button
          type="button"
          className="p-1.5 rounded-md text-muted hover:bg-surface-2"
          aria-label={`Copy ${label}`}
          title="Copy"
          onClick={() => navigator.clipboard.writeText(value).then(() => toast("Copied"))}
        >
          <Copy className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function CompanySso({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const me = useMe();
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [type, setType] = useState<"oidc" | "saml">("oidc");
  const [oidc, setOidc] = useState({ tenantId: "", clientId: "", clientSecret: "" });
  const [metadataUrl, setMetadataUrl] = useState("");
  const [removing, setRemoving] = useState<Idp | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  // The test pop-up reports back here.
  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.data?.type !== "esignit-sso-test") return;
      toast(e.data.message, e.data.ok ? "ok" : "error");
      void reload();
    };
    window.addEventListener("message", on);
    return () => window.removeEventListener("message", on);
  }, [reload]);

  const verified = o.domains.filter((d) => d.verifiedAt);
  const tested = o.idps.some((i) => i.testedAt);
  const blocked = !o.plan.sso ? `Single sign-on is included from the Team plan. Ask eSignIt about upgrading.` : o.company.status !== "active" ? "You can set up single sign-on once eSignIt approves your company." : null;

  return (
    <div className="space-y-5">
      <Card title="1. Verify your email domain">
        <p className="text-sm text-ink-2 mb-3">Prove your company owns its email domain by adding a DNS TXT record. Verified domains route people to your single sign-on, and nobody else can register them on eSignIt.</p>
        <div className="space-y-3">
          {o.domains.map((d) => (
            <div key={d.domain} className="rounded-lg border border-line p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{d.domain}</span>
                {d.verifiedAt ? <Chip tone="good">Verified {fullDate(d.verifiedAt)}</Chip> : <Chip tone="warn">Not verified yet</Chip>}
                <div className="ml-auto flex gap-1.5">
                  {!d.verifiedAt && (
                    <Button className="h-8" busy={busy === `verify:${d.domain}`} onClick={() => run(`verify:${d.domain}`, () => api(`/admin/domains/${encodeURIComponent(d.domain)}/verify`, { body: {} }), `${d.domain} verified`)}>
                      <RefreshCw className="size-3.5" /> Check now
                    </Button>
                  )}
                  <button className="p-2 rounded-lg text-muted hover:bg-surface-2 hover:text-bad" aria-label={`Remove ${d.domain}`} onClick={() => run(`rm:${d.domain}`, () => api(`/admin/domains/${encodeURIComponent(d.domain)}`, { method: "DELETE" }), "Removed")}>
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
              {!d.verifiedAt && (
                <div className="grid sm:grid-cols-3 gap-2 mt-3">
                  <CopyLine label="Type" value={d.record.type} />
                  <CopyLine label="Name (host)" value={d.record.name} />
                  <CopyLine label="Value" value={d.record.value} />
                </div>
              )}
            </div>
          ))}
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void run("add-domain", () => api("/admin/domains", { body: { domain } })).then(() => setDomain(""));
            }}
          >
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourcompany.com" aria-label="Domain" className={cx(inputCls, "sm:w-72")} />
            <Button type="submit" busy={busy === "add-domain"} disabled={!domain.trim()}>
              Add domain
            </Button>
          </form>
        </div>
      </Card>

      <Card title="2. Connect Entra ID (Azure AD)">
        {blocked ? (
          <p className="text-sm text-muted">{blocked}</p>
        ) : (
          <>
            {o.idps.map((i) => (
              <div key={i.name} className="rounded-lg border border-line p-3 mb-3 flex items-center gap-3 flex-wrap">
                <MicrosoftMark className="size-5" />
                <div className="min-w-0">
                  <div className="font-medium">{i.type === "oidc" ? "Entra ID (OpenID Connect)" : "SAML 2.0"}</div>
                  <div className="text-xs text-muted truncate">{i.type === "oidc" ? `Tenant ${i.tenantId} · app ${i.clientId}` : i.metadataUrl}</div>
                </div>
                {i.testedAt ? <Chip tone="good">Tested by {i.testedBy}</Chip> : <Chip tone="warn">Not tested</Chip>}
                <div className="ml-auto flex gap-1.5">
                  <Button className="h-8" onClick={() => startSsoTest(i.name, me.email).then((w) => !w && toast("Allow pop-ups for this site to run the test", "error"))} disabled={!verified.length} title={!verified.length ? "Verify a domain first" : undefined}>
                    <ShieldCheck className="size-3.5" /> Test sign-in
                  </Button>
                  <button className="p-2 rounded-lg text-muted hover:bg-surface-2 hover:text-bad" aria-label="Remove provider" onClick={() => setRemoving(i)}>
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            ))}
            {!verified.length && <p className="text-xs text-warn mb-3">Verify a domain above first: single sign-on only works for addresses at your verified domains.</p>}
            <div className="flex gap-1 mb-3" role="tablist">
              {(["oidc", "saml"] as const).map((t) => (
                <button key={t} role="tab" aria-selected={type === t} onClick={() => setType(t)} className={cx("px-3 h-8 rounded-full text-sm font-medium", type === t ? "bg-accent-chip text-accent-text" : "text-ink-2 hover:bg-surface-2")}>
                  {t === "oidc" ? "OpenID Connect (recommended)" : "SAML"}
                </button>
              ))}
            </div>
            {type === "oidc" ? (
              <div className="grid lg:grid-cols-2 gap-5">
                <ol className="text-sm text-ink-2 space-y-2 list-decimal pl-5">
                  <li>
                    In the Entra admin center, go to <b>App registrations → New registration</b>. Name it <i>eSignIt</i>, choose <b>Accounts in this organizational directory only</b>.
                  </li>
                  <li>
                    Under <b>Redirect URI</b>, choose <b>Web</b> and paste the address below.
                  </li>
                  <li>
                    In <b>Token configuration</b>, add the optional claim <b>email</b> (ID token).
                  </li>
                  <li>
                    In <b>Certificates & secrets</b>, create a client secret and copy its <b>Value</b>.
                  </li>
                  <li>Paste the Directory (tenant) ID, Application (client) ID and secret here, save, then select Test sign-in.</li>
                </ol>
                <div className="space-y-3">
                  <CopyLine label="Redirect URI (Web)" value={o.sp.oidcRedirectUri} />
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run("oidc", () => api("/admin/sso/oidc", { body: oidc }), "Saved. Now test it.").then(() => setOidc({ ...oidc, clientSecret: "" }));
                    }}
                  >
                    <input required placeholder="Directory (tenant) ID" aria-label="Directory (tenant) ID" value={oidc.tenantId} onChange={(e) => setOidc({ ...oidc, tenantId: e.target.value })} className={inputCls} />
                    <input required placeholder="Application (client) ID" aria-label="Application (client) ID" value={oidc.clientId} onChange={(e) => setOidc({ ...oidc, clientId: e.target.value })} className={inputCls} />
                    <input required type="password" autoComplete="off" placeholder="Client secret value" aria-label="Client secret value" value={oidc.clientSecret} onChange={(e) => setOidc({ ...oidc, clientSecret: e.target.value })} className={inputCls} />
                    <Button variant="primary" type="submit" busy={busy === "oidc"}>
                      <KeyRound className="size-4" /> Save OpenID Connect
                    </Button>
                    <p className="text-xs text-muted">The secret goes straight to the sign-in service; eSignIt never shows it again.</p>
                  </form>
                </div>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-5">
                <ol className="text-sm text-ink-2 space-y-2 list-decimal pl-5">
                  <li>
                    In Entra, go to <b>Enterprise applications → New application → Create your own application</b> (non-gallery). Name it <i>eSignIt</i>.
                  </li>
                  <li>
                    Under <b>Single sign-on → SAML</b>, set the Identifier (Entity ID) and Reply URL to the values below.
                  </li>
                  <li>Assign the users or groups who should use eSignIt.</li>
                  <li>
                    Copy the <b>App Federation Metadata Url</b> and paste it here.
                  </li>
                </ol>
                <div className="space-y-3">
                  <CopyLine label="Identifier (Entity ID)" value={o.sp.entityId} />
                  <CopyLine label="Reply URL (ACS)" value={o.sp.replyUrl} />
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run("saml", () => api("/admin/sso/saml", { body: { metadataUrl } }), "Saved. Now test it.");
                    }}
                  >
                    <input required placeholder="App Federation Metadata Url" aria-label="App Federation Metadata Url" value={metadataUrl} onChange={(e) => setMetadataUrl(e.target.value)} className={inputCls} />
                    <Button variant="primary" type="submit" busy={busy === "saml"}>
                      <KeyRound className="size-4" /> Save SAML
                    </Button>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="3. Rules">
        <div className="space-y-3 text-sm">
          <label className={cx("flex items-start gap-2", !tested && "opacity-60")}>
            <input
              type="checkbox"
              className="mt-1"
              disabled={busy !== null || (!tested && !o.company.sso.enforce)}
              checked={o.company.sso.enforce}
              onChange={(e) => run("enforce", () => api("/admin/sso-settings", { body: { enforce: e.target.checked } }), e.target.checked ? "Single sign-on is now required" : "Passwords are allowed again")}
            />
            <span>
              <span className="font-medium text-ink">Require single sign-on</span>
              <span className="block text-xs text-muted">
                People at {verified.map((d) => d.domain).join(", ") || "your verified domains"} can only sign in through Entra. {!me.federated && "Sign in with single sign-on yourself before turning this on, so you can't lock yourself out."}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" disabled={busy !== null} checked={o.company.sso.jit} onChange={(e) => run("jit", () => api("/admin/sso-settings", { body: { jit: e.target.checked } }), "Saved")} />
            <span>
              <span className="font-medium text-ink">Let anyone from Entra in as a sender</span>
              <span className="block text-xs text-muted">Off: only people you invite. On: anyone your Entra app lets through gets a Sender account on first sign-in (counts against your plan).</span>
            </span>
          </label>
          {tested && (
            <p className="text-xs text-good flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5" /> Single sign-on has been tested.
            </p>
          )}
        </div>
      </Card>

      {removing && (
        <ConfirmDialog
          title="Remove this sign-in provider?"
          confirm="Remove"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api(`/admin/sso/${removing.type}`, { method: "DELETE" });
            toast("Removed");
            await reload();
          }}
        >
          <p>People who signed in through it will need a password: they can set one at {location.origin}/join?email=their work email. If single sign-on was required, that's turned off.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
